/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  inject,
  Injector,
  input,
  signal,
  computed,
  OnInit,
  OnDestroy,
  ViewChild,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Overlay, OverlayRef, OverlayConfig, OverlayModule } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CustomErrorDisplayComponent } from '../custom-error-display/custom-error-display.component';
import { SelectOption } from '../input.models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'lib-custom-select',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CustomErrorDisplayComponent,
    TranslateModule,
    OverlayModule,
  ],
  templateUrl: './custom-select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomSelectComponent),
      multi: true,
    },
  ],
})
export class CustomSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
  label = input<string>('');
  name = input<string>('');
  options = input<SelectOption[]>([]);
  placeholder = input<string>('');
  errors = input<{ [key: string]: string }>({});
  displayError = input<boolean>(true);
  outlined = input<boolean>(false);
  placeholderDynamic = input<string>('');
  mb = input<boolean>(true);
  noEmpty = input<boolean>(false);
  disabled = input<boolean>(false);

  private readonly _translate = inject(TranslateService);
  private readonly _injector = inject(Injector);
  private readonly _elementRef = inject(ElementRef);
  private readonly _overlay = inject(Overlay);
  private readonly _viewContainerRef = inject(ViewContainerRef);
  private readonly _cdr = inject(ChangeDetectorRef);

  @ViewChild('dropdownTemplate') _dropdownTemplate!: TemplateRef<any>;
  private _overlayRef: OverlayRef | null = null;
  private _backdropClickSubscription: any;

  _value: any = '';
  _formDisabled = signal(false);
  isDisabled = computed(() => this._formDisabled() || this.disabled());
  isDropdownOpen = false;
  isFocused = false;
  ngControl: NgControl | null = null;

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  get effectivePlaceholder(): string {
    const explicitPlaceholder = this.label() ? 'No seleccionar ' + this.label().toLowerCase() : '';
    if (
      explicitPlaceholder !== undefined &&
      explicitPlaceholder !== null &&
      explicitPlaceholder !== ''
    ) {
      return explicitPlaceholder;
    }
    return this._translate.instant('SHARED.INPUTS_CUSTOM-SELECT.DEFAULT_PLACEHOLDER');
  }

  get effectiveName(): string {
    return this.name() || this.label().toLowerCase().replace(/\s+/g, '-');
  }

  get isInvalid(): boolean {
    if (!this.ngControl?.control) {
      return false;
    }
    const { invalid, touched, dirty } = this.ngControl.control;
    return !!(invalid && (touched || dirty));
  }

  get displayText(): string {
    const currentOptions = this.options();
    const placeholderToDisplay = this.effectivePlaceholder;
    if (this._value !== null && this._value !== undefined && this._value !== '') {
      const selectedOption = currentOptions.find((opt) => opt.value === this._value);
      return selectedOption ? selectedOption.display : placeholderToDisplay;
    } else {
      return placeholderToDisplay;
    }
  }

  // El CDK overlay gestiona los clics fuera automáticamente

  ngOnInit(): void {
    this.ngControl = this._injector.get(NgControl, null);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  writeValue(value: any): void {
    this._value = value;
    this._cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._formDisabled.set(isDisabled);
  }

  toggleDropdown(): void {
    if (this.isDisabled()) return;

    if (this.isDropdownOpen) {
      this._closeDropdown();
    } else {
      this._openDropdown();
    }
  }

  onButtonFocus(): void {
    this.isFocused = true;
  }

  onButtonBlur(): void {
    if (!this.isDropdownOpen) {
      this.isFocused = false;
      this.onTouched();
    }
  }

  private _openDropdown(): void {
    if (this._overlayRef) {
      return;
    }

    const positionStrategy = this._overlay
      .position()
      .flexibleConnectedTo(this._elementRef)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 4,
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
        },
      ]);

    const scrollStrategy = this._overlay.scrollStrategies.reposition();

    const overlayConfig = new OverlayConfig({
      positionStrategy,
      scrollStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      width: this._elementRef.nativeElement.getBoundingClientRect().width,
    });

    this._overlayRef = this._overlay.create(overlayConfig);
    const portal = new TemplatePortal(this._dropdownTemplate, this._viewContainerRef);
    this._overlayRef.attach(portal);

    this._backdropClickSubscription = this._overlayRef.backdropClick().subscribe(() => {
      this._closeDropdown();
      this.onTouched();
    });

    this.isDropdownOpen = true;
  }

  private _closeDropdown(): void {
    if (this._overlayRef) {
      this._overlayRef.detach();
      this._overlayRef.dispose();
      this._overlayRef = null;
    }

    if (this._backdropClickSubscription) {
      this._backdropClickSubscription.unsubscribe();
      this._backdropClickSubscription = null;
    }

    this.isDropdownOpen = false;

    // Si no hay foco en el botón, también actualizamos el estado de foco
    if (!document.activeElement || document.activeElement.id !== this.effectiveName + '-button') {
      this.isFocused = false;
    }

    this.onTouched();
  }

  selectOption(option: SelectOption): void {
    if (this.isDisabled()) return;
    this._value = option.value;
    this.onChange(this._value);
    this.onTouched();
    this._cdr.detectChanges();
    this._closeDropdown();

    // Devolver el foco al botón después de seleccionar una opción
    setTimeout(() => {
      const button = document.getElementById(this.effectiveName + '-button');
      if (button) {
        button.focus();
        this.isFocused = true;
      }
    }, 0);
  }

  selectPlaceholder(): void {
    if (this.isDisabled()) return;
    this._value = null;
    this.onChange(this._value);
    this.onTouched();
    this._cdr.detectChanges();
    this._closeDropdown();

    // Devolver el foco al botón después de seleccionar el placeholder
    setTimeout(() => {
      const button = document.getElementById(this.effectiveName + '-button');
      if (button) {
        button.focus();
        this.isFocused = true;
      }
    }, 0);
  }

  ngOnDestroy(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
      this._overlayRef = null;
    }

    if (this._backdropClickSubscription) {
      this._backdropClickSubscription.unsubscribe();
      this._backdropClickSubscription = null;
    }
  }

  get dynamicPlaceholderText(): string {
    const base = this.placeholderDynamic();

    if (base && this._value !== null && this._value !== undefined && this._value !== '') {
      return `${base} ${this.displayText}`;
    }

    return this.placeholder();
  }

  get hasValue(): boolean {
    return this._value !== null && this._value !== undefined && this._value !== '';
  }
}
